import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Section } from 'src/app/_appCommon/base/AppInterfaceAndBaseClasses';
import { PatientGender } from '../util/PatientData';

/** Datos editables de un paciente, igual en la tienda y en el backoffice. */
export interface PatientEditableData
{
  id: number;
  name: string;
  lastName: string;
  birthday: Date | string;
  gender: PatientGender | null;
  email: string;
  phone: string;
  photoBlobId: number | null;
  photoUrl: string | null;
}

/** Los datos del paciente tal como vienen en el detalle de un caso (CaseViewModel). */
export function patientFromCase(caseEntity: any): PatientEditableData
{
  return {
    id: caseEntity.patientId,
    name: caseEntity.patientName,
    lastName: caseEntity.patientLastName,
    birthday: caseEntity.patientBirthday,
    gender: caseEntity.patientGender ?? null,
    email: caseEntity.patientEmail,
    phone: caseEntity.patientPhone,
    photoBlobId: caseEntity.patientPhotoBlobId ?? null,
    photoUrl: caseEntity.patientPhotoUrl ?? null,
  };
}

/** Los datos del paciente tal como vienen de api/Patient (PatientViewModel). */
export function patientFromPatientModel(patient: any): PatientEditableData
{
  return {
    id: patient.id,
    name: patient.name,
    lastName: patient.lastName,
    birthday: patient.birthday,
    gender: patient.gender ?? null,
    email: patient.email,
    phone: patient.phone,
    photoBlobId: patient.photoBlobId ?? null,
    photoUrl: patient.photoUrl ?? null,
  };
}

/**
 * Guarda la foto y los datos del paciente. Lo comparten la tienda y el backoffice; cada
 * pantalla dice de qué sección viene, porque el servidor lo pide en el header SECTION
 * y con él el interceptor de cada aplicación elige qué token mandar.
 */
// No va en 'root': la tienda y el backoffice cargan aparte y cada uno tiene su propio
// interceptor que agrega el token. Se registra en AppShareModule para que cada módulo que
// lo importa use el HttpClient de su aplicación; desde 'root' las peticiones salían sin sesión.
@Injectable()
export class PatientService
{
  constructor(private http: HttpClient) { }

  /** Sube la foto ya recortada y regresa la ficha del archivo (su id y su dirección). */
  public uploadPhoto(photo: Blob, section: Section): Observable<{ id: number, path: string }>
  {
    const formData = new FormData();
    // Nombre único: el almacenamiento guarda por nombre y dos fotos iguales se pisarían
    formData.append('file', photo, `paciente-${Date.now()}-${Math.floor(Math.random() * 100000)}.jpg`);

    return this.http.post<{ id: number, path: string }>('api/Media/save', formData, { headers: this.sectionHeaders(section) });
  }

  public update(patient: PatientEditableData, section: Section): Observable<any>
  {
    return this.http.put('api/Patient', patient, { headers: this.sectionHeaders(section) });
  }

  private sectionHeaders(section: Section): HttpHeaders
  {
    return new HttpHeaders().append('SECTION', section.toString());
  }
}
